// Feedback / IT-support inbox. Any user can submit; admins mark NEW → PROCESSING
// to queue items for a Claude session that will implement the fixes.
import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useAuthStore } from "@/stores/auth-store";
import { getToken } from "@/lib/auth-token";
import { VoiceInput } from "@/components/voice-input";
import { Bug, Sparkles, MessageSquare, HelpCircle, Upload, Send, PlayCircle, Trash2, Check, X } from "lucide-react";

type FeedbackType = "BUG" | "IMPROVEMENT" | "COMMENT" | "QUESTION";
type FeedbackStatus = "NEW" | "PROCESSING" | "DONE" | "DISMISSED";
type FeedbackPriority = "LOW" | "NORMAL" | "HIGH";

interface Attachment { id: string; fileName: string; mimeType: string | null; sizeBytes: number | null; url: string; }
interface FeedbackItem {
  id: string;
  type: FeedbackType;
  status: FeedbackStatus;
  priority: FeedbackPriority;
  title: string;
  body: string;
  page: string | null;
  resolution: string | null;
  submittedAt: string;
  processedAt: string | null;
  submitter: { id: string; name: string; email: string };
  processor: { id: string; name: string } | null;
  attachments: Attachment[];
}

const TYPE_ICONS: Record<FeedbackType, JSX.Element> = {
  BUG: <Bug className="h-4 w-4 text-red-600" />,
  IMPROVEMENT: <Sparkles className="h-4 w-4 text-amber-600" />,
  COMMENT: <MessageSquare className="h-4 w-4 text-blue-600" />,
  QUESTION: <HelpCircle className="h-4 w-4 text-muted-foreground" />,
};

const STATUS_STYLES: Record<FeedbackStatus, { label: string; variant: "default" | "secondary" | "outline" | "destructive" }> = {
  NEW:        { label: "New",        variant: "default" },
  PROCESSING: { label: "Processing", variant: "secondary" },
  DONE:       { label: "Done",       variant: "outline" },
  DISMISSED:  { label: "Dismissed",  variant: "outline" },
};

export default function FeedbackPage() {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.me);
  const isAdmin = me?.role === "ADMIN" || me?.role === "SUPER_ADMIN";

  const [filter, setFilter] = useState<"all" | FeedbackStatus | "mine">("all");

  // --- Submit form state ---
  const [type, setType] = useState<FeedbackType>("IMPROVEMENT");
  const [priority, setPriority] = useState<FeedbackPriority>("NORMAL");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [page, setPage] = useState("");
  const [pending, setPending] = useState<File[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["/feedback", filter],
    queryFn: async () => {
      const params: any = {};
      if (filter === "mine") params.mineOnly = "true";
      else if (filter !== "all") params.status = filter;
      const { data } = await api.get("/feedback", { params });
      return data as { data: FeedbackItem[]; stats: Record<string, number> };
    },
  });

  const submitMut = useMutation({
    mutationFn: async () => {
      if (!title.trim() || !body.trim()) throw new Error("Title and description are required.");
      const created = await api.post("/feedback", {
        type, priority, title, body,
        page: page || window.location.pathname,
      });
      const feedbackId = created.data.id as string;
      for (const f of pending) {
        const fd = new FormData();
        fd.append("entityType", "Feedback");
        fd.append("entityId", feedbackId);
        fd.append("file", f);
        await api.post("/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
    },
    onSuccess: () => {
      setTitle(""); setBody(""); setPage(""); setPending([]); setSubmitError(null);
      setType("IMPROVEMENT"); setPriority("NORMAL");
      qc.invalidateQueries({ queryKey: ["/feedback"] });
    },
    onError: (err: any) => setSubmitError(err?.response?.data?.message ?? err?.message ?? "Submit failed."),
  });

  const markReadyMut = useMutation({
    mutationFn: async () => (await api.post("/feedback/mark-ready")).data as { moved: number },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["/feedback"] });
      alert(`Moved ${res.moved} items to Processing.\n\nIn your next Claude session, say: "process the feedback queue"`);
    },
  });

  const statusMut = useMutation({
    mutationFn: async ({ id, status, resolution }: { id: string; status?: FeedbackStatus; resolution?: string }) =>
      (await api.patch(`/feedback/${id}`, { status, resolution })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/feedback"] }),
  });

  const delMut = useMutation({
    mutationFn: async (id: string) => { await api.delete(`/feedback/${id}`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/feedback"] }),
  });

  const items = query.data?.data ?? [];
  const stats = query.data?.stats ?? {};

  // --- Paste-an-image support in the textarea ---

  // Cap image dimensions so Claude's API (max 8000px per side) never rejects them.
  async function resizeImageFile(file: File, maxDim = 2048): Promise<File> {
    if (!file.type.startsWith("image/")) return file;
    return new Promise((resolve) => {
      const img = new Image();
      const objUrl = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(objUrl);
        const { width, height } = img;
        if (width <= maxDim && height <= maxDim) { resolve(file); return; }
        const ratio = Math.min(maxDim / width, maxDim / height);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(width * ratio);
        canvas.height = Math.round(height * ratio);
        canvas.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => resolve(blob ? new File([blob], file.name, { type: file.type }) : file),
          file.type,
          0.9,
        );
      };
      img.onerror = () => { URL.revokeObjectURL(objUrl); resolve(file); };
      img.src = objUrl;
    });
  }

  async function onPaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const files: File[] = [];
    for (const it of Array.from(e.clipboardData.items)) {
      if (it.kind === "file") {
        const f = it.getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length) {
      e.preventDefault();
      const resized = await Promise.all(files.map((f) => resizeImageFile(f)));
      setPending((p) => [...p, ...resized]);
    }
  }

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (files.length) {
      const resized = await Promise.all(files.map((f) => resizeImageFile(f)));
      setPending((p) => [...p, ...resized]);
    }
    if (fileRef.current) fileRef.current.value = "";
  }

  return (
    <>
      <PageHeader
        title="Feedback & IT Support"
        description="Report bugs, request improvements, leave comments. Attach screenshots. When ready, an admin marks items for processing."
        actions={
          isAdmin && (
            <Button onClick={() => markReadyMut.mutate()} disabled={markReadyMut.isPending || (stats.NEW ?? 0) === 0}>
              <PlayCircle className="mr-1 h-4 w-4" />
              {markReadyMut.isPending ? "Queuing…" : `Mark ${stats.NEW ?? 0} ready to process`}
            </Button>
          )
        }
      />

      <div className="grid gap-4 p-6 md:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)]">
        {/* Submit form */}
        <Card>
          <CardHeader><CardTitle className="text-base">Submit feedback</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="fb-type">Type</Label>
                <Select id="fb-type" value={type} onChange={(e) => setType(e.target.value as FeedbackType)}>
                  <option value="BUG">Bug</option>
                  <option value="IMPROVEMENT">Improvement</option>
                  <option value="COMMENT">Comment</option>
                  <option value="QUESTION">Question</option>
                </Select>
              </div>
              <div>
                <Label htmlFor="fb-pri">Priority</Label>
                <Select id="fb-pri" value={priority} onChange={(e) => setPriority(e.target.value as FeedbackPriority)}>
                  <option value="LOW">Low</option>
                  <option value="NORMAL">Normal</option>
                  <option value="HIGH">High</option>
                </Select>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="fb-title">Title</Label>
                <VoiceInput value={title} onChange={setTitle} title="Dictate title" />
              </div>
              <Input id="fb-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Short summary" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="fb-body">Description</Label>
                <VoiceInput value={body} onChange={setBody} title="Dictate description" />
              </div>
              <textarea
                id="fb-body"
                rows={5}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                onPaste={onPaste}
                placeholder="What happened, what did you expect, steps to reproduce… You can paste screenshots directly here, or tap the mic to dictate."
                className="flex w-full rounded-md border bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>
            <div>
              <Label htmlFor="fb-page">Page (optional)</Label>
              <Input
                id="fb-page"
                value={page}
                onChange={(e) => setPage(e.target.value)}
                placeholder={window.location.pathname}
              />
            </div>
            <div>
              <Label>Attachments <span className="font-normal text-muted-foreground">(optional)</span> {pending.length > 0 && <span className="text-muted-foreground">· {pending.length} staged</span>}</Label>
              <div className="mt-1 flex flex-wrap gap-2">
                {pending.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-md border bg-muted/40 px-2 py-1 text-xs">
                    {f.type.startsWith("image/") && (
                      <img src={URL.createObjectURL(f)} alt="" className="h-8 w-8 rounded object-cover" />
                    )}
                    <span className="max-w-[160px] truncate">{f.name}</span>
                    <button onClick={() => setPending((p) => p.filter((_, j) => j !== i))}>
                      <X className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                    </button>
                  </div>
                ))}
              </div>
              <input ref={fileRef} type="file" multiple onChange={onPick} className="hidden" />
              <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => fileRef.current?.click()}>
                <Upload className="mr-1 h-4 w-4" />Add files
              </Button>
              <p className="mt-1 text-xs text-muted-foreground">Screenshots help but aren't required. Tip: paste directly into the description to attach.</p>
            </div>
            {submitError && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{submitError}</p>}
            <Button onClick={() => submitMut.mutate()} disabled={submitMut.isPending}>
              <Send className="mr-1 h-4 w-4" />
              {submitMut.isPending ? "Submitting…" : "Submit"}
            </Button>
          </CardContent>
        </Card>

        {/* List */}
        <div className="space-y-3">
          <div className="flex flex-wrap gap-1 text-xs">
            <FilterPill label={`All (${items.length})`} active={filter === "all"} onClick={() => setFilter("all")} />
            <FilterPill label={`New (${stats.NEW ?? 0})`} active={filter === "NEW"} onClick={() => setFilter("NEW")} />
            <FilterPill label={`Processing (${stats.PROCESSING ?? 0})`} active={filter === "PROCESSING"} onClick={() => setFilter("PROCESSING")} />
            <FilterPill label={`Done (${stats.DONE ?? 0})`} active={filter === "DONE"} onClick={() => setFilter("DONE")} />
            <FilterPill label={`Dismissed (${stats.DISMISSED ?? 0})`} active={filter === "DISMISSED"} onClick={() => setFilter("DISMISSED")} />
            <FilterPill label="Mine only" active={filter === "mine"} onClick={() => setFilter("mine")} />
          </div>

          {query.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {!query.isLoading && items.length === 0 && (
            <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
              Nothing here. Submit feedback on the left.
            </p>
          )}

          {items.map((it) => (
            <FeedbackCard
              key={it.id}
              item={it}
              canModerate={isAdmin || it.submitter.id === me?.id}
              isAdmin={isAdmin}
              onStatus={(status, resolution) => statusMut.mutate({ id: it.id, status, resolution })}
              onDelete={() => { if (confirm(`Delete "${it.title}"?`)) delMut.mutate(it.id); }}
            />
          ))}
        </div>
      </div>
    </>
  );
}

function FilterPill({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-md border px-2 py-1 ${active ? "border-primary bg-primary/10 text-primary" : "bg-background hover:bg-muted"}`}
    >
      {label}
    </button>
  );
}

function FeedbackCard({
  item, canModerate, isAdmin, onStatus, onDelete,
}: {
  item: FeedbackItem;
  canModerate: boolean;
  isAdmin: boolean;
  onStatus: (status: FeedbackStatus, resolution?: string) => void;
  onDelete: () => void;
}) {
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolution, setResolution] = useState(item.resolution ?? "");

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {TYPE_ICONS[item.type]}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h4 className="font-medium">{item.title}</h4>
              <Badge variant={STATUS_STYLES[item.status].variant}>{STATUS_STYLES[item.status].label}</Badge>
              {item.priority === "HIGH" && <Badge variant="destructive" className="text-[10px]">High</Badge>}
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              {item.submitter.name} · {new Date(item.submittedAt).toLocaleString()}
              {item.page && <> · <code className="rounded bg-muted px-1 py-0.5">{item.page}</code></>}
            </div>
            <p className="mt-2 whitespace-pre-wrap text-sm">{item.body}</p>

            {item.attachments.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {item.attachments.map((a) => (
                  <AuthedImage key={a.id} attachment={a} />
                ))}
              </div>
            )}

            {item.resolution && (
              <div className="mt-3 rounded-md border-l-2 border-primary/40 bg-primary/5 p-2 text-xs">
                <div className="font-semibold text-primary">Resolution</div>
                <p className="whitespace-pre-wrap">{item.resolution}</p>
                {item.processor && <div className="mt-1 text-muted-foreground">— {item.processor.name}{item.processedAt ? ` · ${new Date(item.processedAt).toLocaleString()}` : ""}</div>}
              </div>
            )}

            {canModerate && (
              <div className="mt-3 flex flex-wrap gap-1">
                {isAdmin && item.status === "NEW" && (
                  <Button size="sm" variant="outline" onClick={() => onStatus("PROCESSING")}>Move to Processing</Button>
                )}
                {isAdmin && (item.status === "NEW" || item.status === "PROCESSING") && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => setResolveOpen((o) => !o)}>
                      <Check className="mr-1 h-3 w-3" />Mark done
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => onStatus("DISMISSED", "Dismissed by admin.")}>Dismiss</Button>
                  </>
                )}
                <Button size="sm" variant="ghost" onClick={onDelete}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}

            {resolveOpen && (
              <div className="mt-2 rounded-md border bg-muted/30 p-2">
                <Label htmlFor={`res-${item.id}`} className="text-xs">Resolution note</Label>
                <textarea
                  id={`res-${item.id}`}
                  rows={2}
                  value={resolution}
                  onChange={(e) => setResolution(e.target.value)}
                  className="mt-1 w-full rounded-md border bg-background px-2 py-1 text-sm"
                  placeholder="What was done?"
                />
                <div className="mt-1 flex gap-1">
                  <Button size="sm" onClick={() => { onStatus("DONE", resolution); setResolveOpen(false); }}>Save & close</Button>
                  <Button size="sm" variant="ghost" onClick={() => setResolveOpen(false)}>Cancel</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/** Loads an attachment image through the auth-guarded stream endpoint. */
function AuthedImage({ attachment }: { attachment: Attachment }) {
  const [url, setUrl] = useState<string | null>(null);
  const isImage = attachment.mimeType?.startsWith("image/");

  async function load() {
    const token = getToken();
    const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api/v1";
    const res = await fetch(`${base}/attachments/${attachment.id}/file`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) return;
    const blob = await res.blob();
    setUrl(URL.createObjectURL(blob));
  }

  if (!isImage) {
    return (
      <Button variant="outline" size="sm" onClick={load}>
        <Upload className="mr-1 h-3 w-3 rotate-180" />{attachment.fileName}
      </Button>
    );
  }

  if (!url) {
    return (
      <button
        onClick={load}
        className="flex h-20 w-32 items-center justify-center rounded-md border bg-muted/50 text-xs text-muted-foreground hover:bg-muted"
      >
        Load image
      </button>
    );
  }

  return (
    <a href={url} target="_blank" rel="noreferrer">
      <img src={url} alt={attachment.fileName} className="h-32 rounded-md border object-cover" />
    </a>
  );
}
